import React, { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ContentRequired from "@/components/ContentRequired";
import { Plus, Trash2, ChevronLeft, Edit2, FileText, ExternalLink, Megaphone, Linkedin } from "lucide-react";
import { toast } from "sonner";
import TeamsReleaseCard from "@/components/TeamsReleaseCard";

function StudentImpersonate() {
  const [students, setStudents] = React.useState([]);
  const [pick, setPick] = React.useState("");
  React.useEffect(() => {
    api.get("/users?role=student")
      .then(({ data }) => setStudents((data || []).filter((u) => u.role === "student" && u.team_id)))
      .catch(() => setStudents([]));
  }, []);
  const open = (path) => {
    if (!pick) return;
    window.open(`${path}?preview_as=${pick}`, "_blank", "noreferrer");
  };
  if (students.length === 0) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="session2-impersonate-picker">
      <span className="text-[#7A5B17] font-semibold">or preview as a specific student:</span>
      <select
        value={pick}
        onChange={(e) => setPick(e.target.value)}
        data-testid="session2-impersonate-select"
        className="h-8 border border-[#f4b847] bg-white px-2 text-xs"
      >
        <option value="">— Pick a student —</option>
        {students.map((s) => (
          <option key={s.id} value={s.id}>{s.name || s.email}</option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => open("/my-team")}
        disabled={!pick}
        data-testid="session2-impersonate-team-btn"
        className="text-[#2596be] underline hover:no-underline disabled:opacity-50"
      >
        My team →
      </button>
      <button
        type="button"
        onClick={() => open("/my-sponsor")}
        disabled={!pick}
        data-testid="session2-impersonate-sponsor-btn"
        className="text-[#2596be] underline hover:no-underline disabled:opacity-50"
      >
        My sponsor →
      </button>
    </div>
  );
}

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function DayDetail() {
  const { id } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const [day, setDay] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [sessions, setSessions] = useState([]);
  const [resources, setResources] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [revealed, setRevealed] = useState(false);
  // Allow deep-linking to a specific tab via hash, e.g. /workbook/:id#sessions
  const initialTab = ["overview", "sessions", "outputs"].includes(
    (location.hash || "").replace("#", "")
  )
    ? location.hash.replace("#", "")
    : "overview";
  const [activeTab, setActiveTab] = useState(initialTab);
  useEffect(() => {
    const h = (location.hash || "").replace("#", "");
    if (["overview", "sessions", "outputs"].includes(h)) setActiveTab(h);
  }, [location.hash]);
  const isAdmin = user && (user.role === "admin" || user.role === "faculty");

  const load = async () => {
    const [d, r, sp, prog] = await Promise.all([
      api.get(`/days/${id}`).then((x) => x.data),
      api.get("/resources").then((x) => x.data),
      api.get("/speakers").then((x) => x.data).catch(() => []),
      api.get("/programme").then((x) => x.data).catch(() => ({})),
    ]);
    setDay(d);
    setForm(d);
    setSessions(d.sessions || []);
    setResources(r.filter((x) => x.day_id === id));
    setRevealed(!!prog?.assignments_revealed);
    const sessSpeakers = new Set(
      (d.sessions || []).map((s) => (s.speaker || "").trim().toLowerCase()).filter(Boolean)
    );
    setSpeakers(sp.filter((s) =>
      s.day_id === id || sessSpeakers.has((s.name || "").trim().toLowerCase())
    ));
  };

  useEffect(() => { load(); }, [id]);

  if (!day) return <div className="text-sm text-[#525252]">Loading…</div>;

  const saveDay = async () => {
    const payload = { ...form };
    delete payload.id; delete payload.sessions; delete payload.created_at; delete payload.updated_at;
    payload.day_number = Number(payload.day_number);
    await api.put(`/days/${id}`, payload);
    toast.success("Day updated");
    setEditing(false);
    load();
  };

  const sortSessionsByTime = (arr) => {
    const toMinutes = (t) => {
      if (!t) return Number.POSITIVE_INFINITY; // blanks go to end
      const m = String(t).trim().match(/^(\d{1,2}):?(\d{2})?/);
      if (!m) return Number.POSITIVE_INFINITY;
      return parseInt(m[1], 10) * 60 + parseInt(m[2] || "0", 10);
    };
    return [...arr].sort((a, b) => toMinutes(a.time_start) - toMinutes(b.time_start));
  };

  const saveSessions = async () => {
    const sorted = sortSessionsByTime(sessions);
    setSessions(sorted);
    await api.put(`/days/${id}/sessions`, sorted.map(({ id, ...rest }) => rest));
    toast.success("Sessions saved (sorted by start time)");
    load();
  };

  const sortNow = () => {
    setSessions((s) => sortSessionsByTime(s));
    toast.success("Sessions reordered by start time");
  };

  const addSession = () => setSessions((s) => [...s, { title: "", time_start: "", time_end: "", speaker: "", location: "", description: "" }]);
  const updateSession = (i, k, v) => setSessions((s) => s.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const removeSession = (i) => setSessions((s) => s.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-8" data-testid="day-detail-page">
      <Link to="/workbook" className="inline-flex items-center text-sm text-[#525252] hover:text-[#0A0A0A]" data-testid="back-to-workbook">
        <ChevronLeft size={16} /> Back to week
      </Link>

      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="label-eyebrow">Day {day.day_number}{day.date ? ` · ${day.date}` : ""}</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">
            {day.title || <span className="italic text-[#525252]">Untitled day</span>}
          </h1>
          {day.theme && <div className="mt-2 text-[#2596be] font-medium">{day.theme}</div>}
          {day.room && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 bg-[#E6F4FA] border border-[#2596be] text-[#1e7da3] text-xs uppercase tracking-[0.16em] font-semibold"
                 data-testid="day-room-badge">
              Room {day.room}
            </div>
          )}
          {day.content_edited_by && (
            <div className="mt-3 text-[11px] text-[#525252] italic" data-testid="day-audit-line">
              Last edited by <strong>{day.content_edited_by}</strong>{day.updated_at ? ` on ${String(day.updated_at).slice(0,10)}` : ""}
            </div>
          )}
        </div>
        {isAdmin && (
          <Button variant="outline" className="rounded-none border-[#0A0A0A]" onClick={() => setEditing((v) => !v)} data-testid="day-edit-toggle">
            <Edit2 size={14} className="mr-2" /> {editing ? "Cancel edit" : "Edit day"}
          </Button>
        )}
      </div>

      {editing && (
        <div className="border border-[#0A0A0A] bg-white p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Day number" type="number" value={form.day_number} onChange={(v) => setForm((f) => ({ ...f, day_number: v }))} />
          <Field label="Date" value={form.date || ""} onChange={(v) => setForm((f) => ({ ...f, date: v }))} placeholder="2026-06-02" />
          <Field label="Title" value={form.title || ""} onChange={(v) => setForm((f) => ({ ...f, title: v }))} />
          <Field label="Theme" value={form.theme || ""} onChange={(v) => setForm((f) => ({ ...f, theme: v }))} />
          <Field label="Room / delivery" value={form.room || ""} onChange={(v) => setForm((f) => ({ ...f, room: v }))} />
          <div className="md:col-span-2">
            <Label className="label-eyebrow">Objective</Label>
            <Textarea value={form.objective || ""} onChange={(e) => setForm((f) => ({ ...f, objective: e.target.value }))}
                      className="mt-2 rounded-none min-h-[80px]" />
          </div>
          <div className="md:col-span-2">
            <Label className="label-eyebrow">Description / day notes</Label>
            <Textarea value={form.description || ""} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                      className="mt-2 rounded-none min-h-[100px]" />
          </div>
          <div className="md:col-span-2">
            <Button onClick={saveDay} className="rounded-none bg-[#2596be] hover:bg-[#1e7da3]" data-testid="day-save-btn">Save day</Button>
          </div>
        </div>
      )}

      {isAdmin && day?.day_number === 1 && (
        <div className="mb-6"><TeamsReleaseCard /></div>
      )}
      {isAdmin && day?.day_number === 2 && (
        <div className="mb-6 border-2 border-[#f4b847] bg-[#FEF9E7] p-5" data-testid="session2-reveal-card">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <div className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#7A5B17]">Session 2 · Reveal</div>
              <div className="font-display text-2xl font-black tracking-tighter mt-1 text-[#0F1F2E]">
                {revealed ? "Team assignments revealed" : "Reveal team assignments"}
              </div>
              <p className="text-xs text-[#525252] mt-2 max-w-2xl">
                Before you flip this switch, students and mentors do not see which team
                is paired with which mentor or business. Press once at the end of Session 2
                — every student sees their mentor, business and challenge statement instantly.
              </p>
            </div>
            <Button
              onClick={async () => {
                try {
                  await api.put("/programme", { assignments_revealed: !revealed });
                  toast.success(!revealed ? "Assignments revealed to all students" : "Assignments hidden again");
                  setRevealed(!revealed);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Could not update");
                }
              }}
              data-testid="session2-reveal-btn"
              className={revealed ? "bg-[#0A0A0A] hover:bg-[#333] rounded-none" : "bg-[#25AAAA] hover:bg-[#1e7d7d] rounded-none"}
            >
              {revealed ? "Hide assignments again" : "Reveal team assignments"}
            </Button>
          </div>
          <div className="mt-3 flex flex-wrap gap-4 text-xs items-center">
            <a href="/my-team?preview=student" target="_blank" rel="noreferrer"
               className="text-[#7A5B17] underline hover:no-underline"
               data-testid="session2-preview-team-link">
              Preview /my-team as student →
            </a>
            <a href="/my-sponsor?preview=student" target="_blank" rel="noreferrer"
               className="text-[#7A5B17] underline hover:no-underline"
               data-testid="session2-preview-sponsor-link">
              Preview /my-sponsor as student →
            </a>
            <StudentImpersonate />
          </div>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="rounded-none bg-white border border-[#E5E7EB] p-0 h-auto">
          <TabsTrigger value="overview" className="rounded-none data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white px-4 py-2 text-xs uppercase tracking-wider" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="sessions" className="rounded-none data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white px-4 py-2 text-xs uppercase tracking-wider" data-testid="tab-sessions">Sessions</TabsTrigger>
          <TabsTrigger value="outputs" className="rounded-none data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white px-4 py-2 text-xs uppercase tracking-wider" data-testid="tab-outputs">Outputs</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* 1. Objective of the day */}
          {day.objective ? (
            <Section label="The objective of the day" body={day.objective} />
          ) : (
            <ContentRequired title="Day objective missing" needed="One-line objective for the day" where="Top of this day page" format="Plain text" testid="day-objective-content-required" />
          )}

          {/* 2. Speakers on the day */}
          {speakers.length > 0 && (
            <section className="border border-[#2596be] bg-[#E6F4FA] p-5" data-testid="day-speakers-today">
              <div className="flex items-center gap-2">
                <Megaphone size={14} className="text-[#1e7da3]" />
                <div className="label-eyebrow text-[#1e7da3]">
                  Speakers on Day {day.day_number}
                </div>
              </div>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {speakers.map((s) => (
                  <article key={s.id} className="bg-white border border-[#2596be]/40 p-3 flex items-start gap-3"
                           data-testid={`day-speaker-${s.id}`}>
                    {s.photo_url ? (
                      <img src={s.photo_url} alt={s.name} className="w-12 h-12 object-cover bg-[#F3F4F6] shrink-0" />
                    ) : (
                      <div className="w-12 h-12 bg-[#f4b847] text-[#0A0A0A] flex items-center justify-center text-sm font-bold uppercase shrink-0">
                        {(s.name || "?").slice(0, 2)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-sm font-bold tracking-tight text-[#0A0A0A] truncate">{s.name}</div>
                      {s.session_title && (
                        <div className="text-[11px] text-[#525252] mt-0.5 line-clamp-2">{s.session_title}</div>
                      )}
                      {s.bio && (
                        <p className="text-[11px] text-[#525252] mt-1 line-clamp-3">{s.bio}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-3 mt-1.5 text-[10px] uppercase tracking-wider font-semibold">
                        {s.bio_url && (
                          <a href={s.bio_url} target="_blank" rel="noopener noreferrer"
                             className="inline-flex items-center gap-1 text-[#2596be] hover:text-[#1e7da3]"
                             data-testid={`day-speaker-bio-${s.id}`}>
                            <FileText size={10} /> Bio PDF
                          </a>
                        )}
                        {s.deck_url && (
                          <a href={s.deck_url} target="_blank" rel="noopener noreferrer"
                             className="inline-flex items-center gap-1 text-[#2596be] hover:text-[#1e7da3]">
                            <ExternalLink size={10} /> Deck
                          </a>
                        )}
                        {s.linkedin_url && (
                          <a href={s.linkedin_url} target="_blank" rel="noopener noreferrer"
                             className="inline-flex items-center gap-1 text-[#2596be] hover:text-[#1e7da3]">
                            <Linkedin size={10} /> LinkedIn
                          </a>
                        )}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
              <Link to="/speakers"
                    className="inline-block mt-4 text-[11px] uppercase tracking-wider text-[#1e7da3] hover:text-[#0A0A0A] font-semibold"
                    data-testid="day-speakers-all-link">
                See the full speaker line-up →
              </Link>
            </section>
          )}

          {/* 3. Quick-link tiles to Resources, Frameworks, Mentors */}
          <div className="grid gap-4 md:grid-cols-3">
            <Link to={`/resources?day=${day.id}`}
                  className="block border border-[#E5E7EB] bg-white p-5 hover:border-[#0A0A0A] transition-colors"
                  data-testid="day-link-resources">
              <div className="label-eyebrow text-[#2596be]">Day {day.day_number} resources</div>
              <div className="font-display text-lg font-bold tracking-tight mt-2">
                Open the Resource Library →
              </div>
              <p className="text-xs text-[#525252] mt-1">
                Decks, case studies and references tagged to Day {day.day_number}.
              </p>
            </Link>
            <Link to="/frameworks"
                  className="block border border-[#E5E7EB] bg-white p-5 hover:border-[#0A0A0A] transition-colors"
                  data-testid="day-link-frameworks">
              <div className="label-eyebrow text-[#A23F8F]">Framework tools</div>
              <div className="font-display text-lg font-bold tracking-tight mt-2">
                Build the case →
              </div>
              <p className="text-xs text-[#525252] mt-1">
                Business Model Canvas, Customer Avatar, plus Desirability / Feasibility / Viability audits — and the Awake &amp; Catch external tools.
              </p>
            </Link>
            <Link to="/mentors"
                  className="block border border-[#E5E7EB] bg-white p-5 hover:border-[#0A0A0A] transition-colors"
                  data-testid="day-link-mentors">
              <div className="label-eyebrow text-[#f4b847]">Mentors available</div>
              <div className="font-display text-lg font-bold tracking-tight mt-2">
                See the mentor line-up →
              </div>
              <p className="text-xs text-[#525252] mt-1">
                Expertise, preferred mode and (once published) the day&apos;s mentoring slots.
              </p>
            </Link>
          </div>

          {day.description ? (
            <Section label="Day notes" body={day.description} />
          ) : null}
        </TabsContent>

        <TabsContent value="sessions" className="space-y-4">
          {(!sessions || sessions.length === 0) && !isAdmin && (
            <ContentRequired title="Sessions not yet scheduled" needed="Session titles, timings, speakers, locations" where="Sessions tab" format="One row per session" testid="day-sessions-content-required" />
          )}
          {isAdmin && (
            <div className="space-y-4">
              {sessions.map((s, i) => (
                <div key={i} className="border border-[#E5E7EB] bg-white p-4 grid grid-cols-1 md:grid-cols-6 gap-3" data-testid={`session-row-${i}`}>
                  <Input className="rounded-none md:col-span-2" placeholder="Title" value={s.title || ""} onChange={(e) => updateSession(i, "title", e.target.value)} />
                  <Input className="rounded-none" placeholder="Start" value={s.time_start || ""} onChange={(e) => updateSession(i, "time_start", e.target.value)} />
                  <Input className="rounded-none" placeholder="End" value={s.time_end || ""} onChange={(e) => updateSession(i, "time_end", e.target.value)} />
                  <Input className="rounded-none" placeholder="Speaker" value={s.speaker || ""} onChange={(e) => updateSession(i, "speaker", e.target.value)} />
                  <div className="flex gap-2">
                    <Input className="rounded-none flex-1" placeholder="Room" value={s.location || ""} onChange={(e) => updateSession(i, "location", e.target.value)} />
                    <Button variant="outline" size="icon" className="rounded-none" onClick={() => removeSession(i)} data-testid={`session-delete-${i}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                  <Textarea className="md:col-span-6 rounded-none min-h-[60px]" placeholder="Session description" value={s.description || ""} onChange={(e) => updateSession(i, "description", e.target.value)} />
                </div>
              ))}
              <div className="flex gap-2">
                <Button variant="outline" className="rounded-none" onClick={addSession} data-testid="add-session-btn">
                  <Plus size={14} className="mr-2" /> Add session
                </Button>
                <Button variant="outline" className="rounded-none" onClick={sortNow} data-testid="sort-sessions-btn" title="Reorder rows by start time (does not save)">
                  Sort by time
                </Button>
                <Button onClick={saveSessions} className="rounded-none bg-[#2596be] hover:bg-[#1e7da3]" data-testid="save-sessions-btn">
                  Save sessions
                </Button>
              </div>
            </div>
          )}
          {!isAdmin && sessions && sessions.length > 0 && (
            <div className="border border-[#E5E7EB] bg-white">
              {sortSessionsByTime(sessions).map((s, i) => (
                <div key={i} className="grid grid-cols-12 gap-3 p-4 border-b border-[#E5E7EB] last:border-b-0">
                  <div className="col-span-12 md:col-span-2 label-eyebrow">{s.time_start || "—"} {s.time_end ? `– ${s.time_end}` : ""}</div>
                  <div className="col-span-12 md:col-span-7">
                    <div className="font-display font-semibold">{s.title || "Untitled session"}</div>
                    {s.description && <p className="text-sm text-[#525252] mt-1">{s.description}</p>}
                  </div>
                  <div className="col-span-12 md:col-span-3 text-sm text-[#525252]">
                    {s.speaker && <div>{s.speaker}</div>}
                    {s.location && <div className="text-xs">{s.location}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="outputs" className="space-y-4">
          {resources.length === 0 ? (
            <ContentRequired
              title="No resources attached to this day yet"
              description="Add company packs, slide decks, handouts, reading lists, simulation packs, briefs. Either upload a PDF directly or paste a Dropbox / Google Drive / SharePoint share link — students can click straight through to the file."
              needed="Day-specific resources, briefs, decks"
              where="Outputs tab + linked from Resource Library"
              format="Upload (PDF/DOCX/PPTX) or external URL"
              testid="day-outputs-content-required"
            />
          ) : (
            <div>
              <div className="label-eyebrow mb-3">Day resources ({resources.length})</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {resources.map((r) => {
                  const tokenQuery = `?auth=${encodeURIComponent(localStorage.getItem("weave_token") || "")}`;
                  return (
                    <div key={r.id} className="border border-[#E5E7EB] bg-white p-5 flex flex-col" data-testid={`day-resource-${r.id}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="label-eyebrow">{r.category || "Resource"}</span>
                        {r.external_url && !r.file_id && (
                          <span className="text-[10px] uppercase tracking-wider border border-[#E5E7EB] px-2 py-0.5 text-[#525252]">External link</span>
                        )}
                        {r.file_id && (
                          <span className="text-[10px] uppercase tracking-wider border border-[#E5E7EB] px-2 py-0.5 text-[#525252]">Uploaded</span>
                        )}
                      </div>
                      <div className="font-display text-lg font-bold tracking-tight mt-2">{r.title}</div>
                      {r.description && <p className="text-sm text-[#525252] mt-2 flex-1">{r.description}</p>}
                      <div className="mt-4 flex items-center gap-4 text-sm">
                        {r.file_id && (
                          <a href={`${BACKEND}/api/files/${r.file_id}/download${tokenQuery}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#2596be] hover:underline">
                            <FileText size={14} /> Open file
                          </a>
                        )}
                        {r.external_url && (
                          <a href={r.external_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#2596be] hover:underline">
                            <ExternalLink size={14} /> Open link
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {isAdmin && (
                <Link to="/resources" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider mt-4 text-[#525252] hover:text-[#0A0A0A]" data-testid="day-add-resource-link">
                  <Plus size={12} /> Add another resource for this day
                </Link>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Section({ label, body }) {
  return (
    <div className="border border-[#E5E7EB] bg-white p-6">
      <div className="label-eyebrow">{label}</div>
      <p className="mt-3 text-[#0A0A0A] whitespace-pre-wrap leading-relaxed">{body}</p>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <div>
      <Label className="label-eyebrow">{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)}
             placeholder={placeholder} className="mt-2 rounded-none h-11" />
    </div>
  );
}
